import type { ComponentType, ReactNode } from 'react'
import { CalendarDays, Mail, MapPin, UserRound } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { todayCalendarDate } from '@/lib/patient'
import {
  LANGUAGES,
  SEX_OPTIONS,
  optionsWith,
  type DemographicFormField,
  type DemographicsForm,
} from '@/lib/patientForm'

/**
 * `htmlFor`/`id` are wired through so every input has a real accessible name.
 * That is what screen readers announce, and it is how the e2e suite addresses
 * fields — this area has no test ids by convention.
 */
export function Field({
  id,
  label,
  children,
  required,
  hint,
}: {
  id: string
  label: string
  children: ReactNode
  required?: boolean
  hint?: string
}) {
  return (
    <div className='space-y-1.5'>
      <label htmlFor={id} className='block text-xs font-medium text-muted-foreground'>
        {label}
        {required && <span className='ml-0.5 text-destructive'>*</span>}
      </label>
      {children}
      {hint && <p className='text-[11px] text-muted-foreground'>{hint}</p>}
    </div>
  )
}

export interface DemographicsSectionProps {
  title: string
  description?: string
  icon: ComponentType<{ className?: string }>
  children: ReactNode
}

/**
 * The create page's demographic inputs. The rules behind them live in
 * `lib/patientForm.ts`, shared with the in-place correction on the patient page
 * (z8nrz7dp78 AC2.6).
 */
export function PatientDemographicsFields({
  form,
  onChange,
  birthDateError,
  section: Section,
  identityLead,
  idPrefix = 'patient',
  onBirthDateInvalid,
}: {
  form: DemographicsForm
  onChange: (field: DemographicFormField, value: string) => void
  birthDateError?: string | null
  section: ComponentType<DemographicsSectionProps>
  /** Rendered first in the Identity section: the MRN input. */
  identityLead?: ReactNode
  idPrefix?: string
  /**
   * `max` makes the browser refuse a future date on its own, so the caller's
   * submit-time `birthDateError` check never runs and the operator gets a
   * native bubble instead of the message above. A page whose form does NOT
   * set `noValidate` (PatientCreatePage) passes this to swap one for the
   * other; the submit stays blocked either way. A page that already sets
   * `noValidate` (the in-place correction form) never fires `onInvalid` at
   * all, so passing nothing there is correct.
   */
  onBirthDateInvalid?: () => void
}) {
  const id = (field: string) => `${idPrefix}-${field}`
  const text = (field: DemographicFormField, type?: string) => (
    <Input
      id={id(field)}
      type={type}
      value={form[field]}
      onChange={e => onChange(field, e.target.value)}
    />
  )
  const errorId = id('birthDate-error')

  return (
    <>
      <Section
        title='Identity'
        description='How this patient is identified across the system.'
        icon={UserRound}
      >
        <div className='space-y-4'>
          {identityLead}
          <div className='grid gap-4 sm:grid-cols-2'>
            <Field id={id('firstName')} label='First name'>
              {text('firstName')}
            </Field>
            <Field id={id('lastName')} label='Last name'>
              {text('lastName')}
            </Field>
          </div>
        </div>
      </Section>

      <Section
        title='Demographics'
        description='Used to personalize care and derive age.'
        icon={CalendarDays}
      >
        <div className='grid gap-4 sm:grid-cols-2'>
          <Field id={id('sex')} label='Sex'>
            <Select value={form.sex} onValueChange={v => onChange('sex', v)}>
              <SelectTrigger id={id('sex')}>
                <SelectValue placeholder='Not specified' />
              </SelectTrigger>
              <SelectContent>
                {optionsWith(SEX_OPTIONS, form.sex).map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id={id('birthDate')} label='Date of birth' hint='Age is derived from this.'>
            {/* A calendar date: the input's own YYYY-MM-DD is sent verbatim and
                stored as that day (z8nrz7dp77). */}
            <Input
              type='date'
              value={form.birthDate}
              id={id('birthDate')}
              max={todayCalendarDate()}
              aria-invalid={birthDateError ? true : undefined}
              aria-describedby={birthDateError ? errorId : undefined}
              onChange={e => onChange('birthDate', e.target.value)}
              onInvalid={onBirthDateInvalid && (e => {
                if (!e.currentTarget.validity.rangeOverflow) return
                e.preventDefault()
                onBirthDateInvalid()
              })}
            />
            {birthDateError && (
              <p id={errorId} role='alert' className='text-xs text-destructive'>
                {birthDateError}
              </p>
            )}
          </Field>
          <div className='sm:col-span-2'>
            <Field id={id('preferredLanguage')} label='Preferred language'>
              <Select
                value={form.preferredLanguage}
                onValueChange={v => onChange('preferredLanguage', v)}
              >
                <SelectTrigger id={id('preferredLanguage')}>
                  <SelectValue placeholder='Not specified' />
                </SelectTrigger>
                <SelectContent>
                  {optionsWith(LANGUAGES, form.preferredLanguage).map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      </Section>

      <Section title='Contact' description='How to reach this patient directly.' icon={Mail}>
        <div className='grid gap-4 sm:grid-cols-2'>
          <Field id={id('email')} label='Email'>
            {text('email', 'email')}
          </Field>
          <Field id={id('phone')} label='Phone'>
            {text('phone')}
          </Field>
        </div>
      </Section>

      <Section title='Address' description='Mailing and residential details.' icon={MapPin}>
        <div className='grid gap-4 sm:grid-cols-2'>
          <div className='sm:col-span-2'>
            <Field id={id('addressLine')} label='Street address'>
              {text('addressLine')}
            </Field>
          </div>
          <Field id={id('city')} label='City'>
            {text('city')}
          </Field>
          <Field id={id('state')} label='State'>
            {text('state')}
          </Field>
          <Field id={id('postalCode')} label='Postal code'>
            {text('postalCode')}
          </Field>
          <Field id={id('country')} label='Country'>
            {text('country')}
          </Field>
        </div>
      </Section>
    </>
  )
}
